import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Formik } from "formik";
import { Slide, ToastContainer, toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { yearlyReportsManagement } from "../../../../../../hooks/api/YearlyReportsManagement/YearlyReportsManagement";
import { getNumberOfUsedFilters } from "../../../../../../services/utils/Filter";
import { DefaultLayout } from "../../../../../../layouts/Default";
import { TopNavigation } from "../../../../../../themes/Default/components/TopNavigation";
import { Button, ButtonBack } from "../../../../../../themes/Default/components/TopNavigation/components/Button";
import { Tabs } from "../../../../../../themes/Default/components/Tabs";
import { Main } from "../../../../../../themes/Default/components/Main";
import { Title } from "../../../../../../themes/Default/components/Title";
import { Title as ListTitle } from "../../../../../../themes/Default/components/List/components/Header/components/Title";
import { Breadcrumps } from "../../../../../../themes/Default/components/Breadcrumps";
import { Link } from "../../../../../../themes/Default/components/Breadcrumps/components/Link";
import { List } from "../../../../../../themes/Default/components/List";
import { Menu as ListMenu } from "../../../../../../themes/Default/components/List/components/Header/components/Menu";
import { Body as ListBody } from "../../../../../../themes/Default/components/List/components/Body";
import { Header as ListHeader } from "../../../../../../themes/Default/components/List/components/Header";
import { Filter as ListFilter } from "../../../../../../themes/Default/components/List/components/Header/components/Filter";
import { Button as ListMenuButton } from "../../../../../../themes/Default/components/List/components/Header/components/Menu/components/Button";
import { Form } from "../../../../../../themes/Default/components/Form";
import { Row } from "../../../../../../themes/Default/components/Form/components/Row";
import { Field } from "../../../../../../themes/Default/components/Form/components/Row/components/Field";
import { Input } from "../../../../../../themes/Default/components/Form/components/Input";
import { TableHead } from "../../../../../../themes/Default/components/List/components/Body/components/TableHead";
import { TableRow } from "../../../../../../themes/Default/components/List/components/Body/components/TableHead/components/TableRow";
import { TableHeadCell } from "../../../../../../themes/Default/components/List/components/Body/components/TableHead/components/TableRow/components/TableHeadCell";
import { TableBody } from "../../../../../../themes/Default/components/List/components/Body/components/TableBody";
import { TableDataCell } from "../../../../../../themes/Default/components/List/components/Body/components/TableBody/components/TableDataCell";
import { Checkbox } from "../../../../../../themes/Default/components/Form/components/Checkbox";
import { Pagination } from "../../../../../../themes/Default/components/List/components/Footer/components/Pagination";
import { Toaster } from "../../../../../../themes/Default/components/Toaster";

const Yearly = () => {
    const { t } = useTranslation("common");
    const recordsPerPage = 20;
    const defaultFilters = {
        startPage: 0,
        limitPage: recordsPerPage,
    };
    const formRef = useRef(null);
    const navigate = useNavigate();
    const [filters, setFilters] = useState(defaultFilters);
    const [showFilter, setShowFilter] = useState(false);
    const [numberOfUserdFilters, setNumberOfUserdFilters] = useState();
    const [totalRecords, setTotalRecords] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [disableDelete, setDisableDelete] = useState(true);
    const [selectedReport, setSelectedReport] = useState([]);
    const [reports, setReports] = useState();
    const { useYearlyReportsListMutation, useDeleteYearlyReportsMutation } = yearlyReportsManagement;
    const [reportList, { isLoading }] = useYearlyReportsListMutation();
    const [deleteReport] = useDeleteYearlyReportsMutation();

    const tabs = [
        { title: t("reports.montlhy"), onClick: () => gotoMonthlyTab() },
        { title: t("reports.semiAnnual"), onClick: () => gotoSemiyearlyTab() },
        { title: t("reports.annual"), isActive: true },
    ];

    const gotoMonthlyTab = () => {
        navigate("/settings/reports/monthly");
    };

    const gotoSemiyearlyTab = () => {
        navigate("/settings/reports/semiyearly");
    };

    const openFilter = () => {
        setShowFilter(true);
    };

    const closeFilter = () => {
        setShowFilter(false);
    };

    const gotoNew = () => {
        navigate("/settings/reports/yearly/add");
    };

    const gotoEdit = (reportId) => {
        navigate("/settings/reports/yearly/edit/" + reportId);
    };

    const onFilter = () => {
        if (formRef.current) {
            formRef.current.handleSubmit();
        }
    };

    const onFiltered = (values) => {
        setFilters({
            ...filters,
            startPage: 0,
            title: values.title,
            classNumber: values.classNumber,
        });
    };

    const onResetFilter = () => {
        setFilters(defaultFilters);
        setShowFilter(false);
        if (formRef.current) {
            formRef.current.resetForm();
        }
    };

    const onPageChange = (currentPage) => {
        setCurrentPage(currentPage);
        const newStartPage = (currentPage - 1) * recordsPerPage;
        setFilters({
            ...filters,
            startPage: newStartPage,
            limitPage: recordsPerPage,
        });
    };

    const onSelectionChange = (id) => {
        setSelectedReport((prevSelected) => {
            if (prevSelected.includes(id)) {
                return prevSelected.filter((recordId) => recordId !== id);
            } else {
                return [...prevSelected, id];
            }
        });
    };

    const onDelete = () => {
        toast.warn(
            ({ closeToast }) => (
                <Toaster
                    closeToast={closeToast}
                    description={t("areYouSure")}
                    optionNo={t("no")}
                    optionYes={t("yes")}
                    onClick={() => {
                        closeToast();
                        for (let i = 0; i < selectedReport?.length; i++) {
                            const reportId = selectedReport[i];
                            deleteReport(reportId).then((result) => {
                                if (result?.error) {
                                    toast.error(result.error.data.error.responseMessage);
                                } else {
                                    reportList(filters).then((result) => {
                                        const reports = result?.data?.records;
                                        setReports(reports);
                                        setTotalRecords(result?.data?.totalRecords);
                                    });
                                    setDisableDelete(true);
                                    toast.info(result?.data?.info?.responseMessage);
                                    onSelectionChange(reportId);
                                }
                            });
                        }
                    }}
                />
            ),
            {
                autoClose: false,
                progress: false,
                closeButton: false,
            },
        );
    };

    useEffect(() => {
        if (selectedReport.length > 0) {
            setDisableDelete(false);
        } else {
            setDisableDelete(true);
        }
    }, [selectedReport]);

    useEffect(() => {
        reportList(filters).then((result) => {
            const reports = result?.data?.records;
            setReports(reports);
            setTotalRecords(result?.data?.totalRecords);
        });
        setNumberOfUserdFilters(getNumberOfUsedFilters(filters));
    }, [filters, reportList]);

    return (
        <DefaultLayout>
            
            <TopNavigation>
                <ButtonBack to="/settings" />
                <Button title={t("reports.addNew")} icon="fa-regular fa-plus" onClick={gotoNew}></Button>
                <Button title={t("delete")} icon="fa-regular fa-trash-can" isDisabled={disableDelete} onClick={onDelete}></Button>
            </TopNavigation>
            <Tabs tabs={tabs} />
            <Main>
                <Title>{`${t("reports.title")} (${totalRecords})`}</Title>
                <Breadcrumps>
                    <Link title={t("reports.title")} icon="fa-gear" />
                    <Link title={t("reports.annual")} />
                    <Link title={t("settings.list")} isActive={true} />
                </Breadcrumps>
                {!isLoading && (
                    <>
                        <List>
                            <ListHeader>
                                <ListMenu>
                                    <ListMenuButton title="Filter" icon="fa-filter" onClick={openFilter} badge={numberOfUserdFilters} />
                                </ListMenu>
                                <ListFilter isVisible={showFilter} onClose={closeFilter} onFilter={onFilter} onResetFilter={onResetFilter}>
                                    <Formik
                                        innerRef={formRef}
                                        initialValues={{ title: filters?.title, classNumber: filters?.classNumber }}
                                        onSubmit={onFiltered}
                                    >
                                        {({ values, handleChange, handleSubmit, resetForm }) => (
                                            <Form>
                                                <Row>
                                                    <Field>
                                                        <Input name="title" label={t("reports.name")} value={values.title} onChange={handleChange} />
                                                    </Field>
                                                </Row>
                                                <Row>
                                                    <Field>
                                                        <Input
                                                            name="classNumber"
                                                            label={t("reports.classNumber")}
                                                            value={values.classNumber}
                                                            onChange={handleChange}
                                                        />
                                                    </Field>
                                                </Row>
                                            </Form>
                                        )}
                                    </Formik>
                                </ListFilter>
                            </ListHeader>
                            <ListBody>
                                <TableHead>
                                    <TableRow>
                                        <TableHeadCell></TableHeadCell>
                                        <TableHeadCell>
                                            <span>{t("reports.name")}</span>
                                        </TableHeadCell>
                                        <TableHeadCell>
                                            <span>{t("reports.classNumber")}</span>
                                        </TableHeadCell>
                                        <TableHeadCell>
                                            <span>{t("reports.language")}</span>
                                        </TableHeadCell>
                                        <TableHeadCell>
                                            <span>{t("reports.program")}</span>
                                        </TableHeadCell>
                                        <TableHeadCell></TableHeadCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {reports?.length > 0 &&
                                        reports?.map((report, index) => {
                                            return (
                                                <TableRow key={report.id}>
                                                    <TableDataCell>
                                                        <Checkbox name="isBlockClass" value={report.id} onChange={() => onSelectionChange(report.id)} />
                                                    </TableDataCell>
                                                    <TableDataCell>{report.title}</TableDataCell>
                                                    <TableDataCell>{report.classNumber}</TableDataCell>
                                                    <TableDataCell>{report.languageName}</TableDataCell>
                                                    <TableDataCell>{report.languageProgramName}</TableDataCell>
                                                    <TableDataCell>
                                                        <Button icon="fa-regular fa-pen" onClick={() => gotoEdit(report.id)}></Button>
                                                    </TableDataCell>
                                                </TableRow>
                                            );
                                        })}
                                </TableBody>
                            </ListBody>
                        </List>
                        <Pagination currentPage={currentPage} recordsPerPage={recordsPerPage} totalRecords={totalRecords} onClick={onPageChange} />
                    </>
                )}
            </Main>
        </DefaultLayout>
    );
};

export default Yearly;
